import { useCallback } from "react";
import type { ArrayUpdateAction } from "@shared/utils/array/updateArrayWithAction";
import { arrayAction, updateArrayWithAction } from "@shared/utils/array/updateArrayWithAction";

/**
 * Состояние списка: текущий массив и функция обновления.
 * @template TData тип элемента списка
 */
export interface ListState<TData> {
    data: TData[] | undefined;
    setData: (updater: TData[] | ((prev: TData[] | undefined) => TData[])) => void;
}

/**
 * Методы обновления списка по id через {@link updateArrayWithAction}.
 * @template TData тип элемента списка
 */
export interface ListMutate<TData> {
    applyAction: (action: ArrayUpdateAction<TData>) => void;
    deleteItem: (id: string | number) => void;
    deleteItems: (ids: (string | number)[]) => void;
    updateItem: (item: TData) => void;
    updateItems: (items: TData[]) => void;
    createItem: (item: TData, index?: number) => void;
    createItems: (items: TData[]) => void;
}

/**
 * Возвращает методы обновления списка по паре «массив + setter».
 * Подходит для {@link useQueryData}, `useState` и {@link useBusinessListQuery}.
 *
 * @template TData тип элемента списка
 * @param listState объект с `data` и `setData`
 * @param getId функция получения id элемента
 */
export function useListMutate<TData>(
    listState: ListState<TData>,
    getId: (item: TData) => string | number,
): ListMutate<TData> {
    const applyAction = useCallback(
        (action: ArrayUpdateAction<TData>) => {
            const current = listState.data ?? [];
            listState.setData(updateArrayWithAction(current, action, getId));
        },
        [listState, getId],
    );

    const deleteItem = useCallback((id: string | number) => applyAction(arrayAction.delete(id)), [applyAction]);
    const deleteItems = useCallback((ids: (string | number)[]) => applyAction(arrayAction.deleteMany(ids)), [applyAction]);
    const updateItem = useCallback((item: TData) => applyAction(arrayAction.update(item)), [applyAction]);
    const updateItems = useCallback((items: TData[]) => applyAction(arrayAction.updateMany(items)), [applyAction]);
    const createItem = useCallback(
        (item: TData, index?: number) => applyAction(arrayAction.create(item, index)),
        [applyAction],
    );
    const createItems = useCallback((items: TData[]) => applyAction(arrayAction.createMany(items)), [applyAction]);

    return {
        applyAction,
        deleteItem,
        deleteItems,
        updateItem,
        updateItems,
        createItem,
        createItems,
    };
}
